import { Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { PageSection } from "~/components/ui/page-section";

import { groupGoals, type GoalItem } from "./goal-groups";
import { DoneGoals, GoalRows, goalCount, type RowProps } from "./goal-list";

/**
 * The goals of one category, on its page: open goals first, then the
 * finished ones in a closed "Done" section. The client and her coach see the
 * same rows; only the client can add a goal.
 */
export function CategoryGoals({
  categoryName,
  items,
  isLoading,
  isError,
  onRetry,
  onAddGoal,
  ...rowProps
}: RowProps & {
  /** The decrypted name, or null while it decrypts. */
  categoryName: string | null;
  /** This category's goals. */
  items: readonly GoalItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onAddGoal?: () => void;
}) {
  const groups = groupGoals(items);
  const open = groups.categories.flatMap((group) => group.goals);

  const renderGoals = () => {
    if (isLoading) {
      return (
        <Card className="gap-0 divide-y divide-gray-100 p-0" aria-hidden>
          {[1, 2].map((i) => (
            <div key={i} className="space-y-2 px-4 py-3">
              <div className="h-5 w-48 animate-pulse rounded bg-gray-100" />
              <div className="h-4 w-32 animate-pulse rounded bg-gray-100" />
            </div>
          ))}
        </Card>
      );
    }
    if (isError) {
      return (
        <p className="flex flex-wrap items-center gap-2 text-sm text-red-700">
          The goals did not load.
          <Button
            variant="link"
            size="sm"
            className="h-auto px-0 text-red-700"
            onClick={onRetry}
          >
            Try again
          </Button>
        </p>
      );
    }
    if (items.length === 0) {
      return (
        <p className="text-text-secondary text-sm">
          No goals in this category yet.
        </p>
      );
    }
    return (
      <>
        {open.length > 0 && <GoalRows goals={open} {...rowProps} />}
        <DoneGoals goals={groups.done} {...rowProps} />
      </>
    );
  };

  return (
    <PageSection
      title={
        <span className="flex min-w-0 flex-wrap items-baseline gap-x-2 break-words">
          {categoryName === null ? (
            <span className="inline-block h-6 w-48 animate-pulse rounded bg-gray-100" />
          ) : (
            `Goals in ${categoryName}`
          )}
          {!isLoading && !isError && (
            <span className="text-text-tertiary text-sm font-normal">
              {goalCount(items.length)}
            </span>
          )}
        </span>
      }
      action={
        // The button's text stays fixed: the Meta pixel reports it on every
        // click, and the category name is decrypted text.
        onAddGoal && (
          <Button size="sm" onClick={onAddGoal}>
            <Plus aria-hidden />
            Add goal
          </Button>
        )
      }
    >
      {renderGoals()}
    </PageSection>
  );
}
